// Extensions that name the same file type; compared lower-cased.
const EQUIVALENT_EXTENSIONS: {[extension: string]: string} = {
  jpeg: 'jpg',
  tiff: 'tif',
};

// Characters Windows / macOS refuse in a filename, plus C0 control characters.
const ILLEGAL_FILENAME_CHARS = /[\\/:*?"<>|\u0000-\u001F]/g;

function normaliseExtension(extension: string): string {
  const lower = extension.toLowerCase();
  return EQUIVALENT_EXTENSIONS[lower] || lower;
}

// Strips trailing dots and whitespace with a backwards scan; the equivalent regex,
// /[.\s]+$/, backtracks quadratically on long runs of them (SonarQube S5852).
function trimTrailingDotsAndSpaces(name: string): string {
  let end = name.length;
  while (end > 0 && (name[end - 1] === '.' || /\s/.test(name[end - 1]))) {
    end--;
  }
  return name.substring(0, end);
}

function sanitiseName(name: string): string {
  return trimTrailingDotsAndSpaces(name.replace(ILLEGAL_FILENAME_CHARS, '_').trim());
}

/**
 * Download filename for a chat attachment: `[<prefix>_]<name>.<extension>`.
 *
 * - <extension> is always the attachment id's (after its last dot). The server assigns it and the
 *   app types the file by it, so the uploaded filename's own suffix is dropped only when it names
 *   the same type (case-insensitive, jpg = jpeg, tif = tiff); any other suffix (`v1.2`,
 *   `notes.final`) is part of the name, and a truncated filename simply gets the id's extension.
 * - <name> is the uploaded filename, so a file downloads under the name people saw in the chat.
 *   Characters illegal in a filename (`\ / : * ? " < > |`, control characters) become `_`, and
 *   surrounding whitespace and trailing dots are trimmed: browsers would mangle them, and
 *   client-zip writes zip entry names verbatim, where `/` would even create a folder. A missing
 *   filename, or one that sanitises to nothing, falls back to the id's name (before its first dot).
 * - `usedNames` holds the names already taken in the same download (a zip). A clash, compared
 *   case-insensitively as the file systems do, gets ` (n)` added; the set is updated in place.
 */
export function buildAttachmentDownloadName(
  attachment: {attachment_id: string; filename?: string},
  options: {prefix?: string; usedNames?: Set<string>} = {}
): string {
  const attachmentId = attachment.attachment_id;
  const firstDot = attachmentId.indexOf('.');
  const lastDot = attachmentId.lastIndexOf('.');
  const idName = firstDot >= 0 ? attachmentId.substring(0, firstDot) : attachmentId;
  const idExtension = lastDot >= 0 ? attachmentId.substring(lastDot + 1) : '';

  let name = (attachment.filename || '').trim();
  const filenameDot = name.lastIndexOf('.');
  if (filenameDot > 0 && idExtension &&
    normaliseExtension(name.substring(filenameDot + 1)) === normaliseExtension(idExtension)) {
    name = name.substring(0, filenameDot);
  }
  name = sanitiseName(name) || idName;

  const base = options.prefix ? `${options.prefix}_${name}` : name;
  const suffix = idExtension ? `.${idExtension}` : '';

  let downloadName = `${base}${suffix}`;
  const usedNames = options.usedNames;
  if (usedNames) {
    for (let n = 1; usedNames.has(downloadName.toLowerCase()); n++) {
      downloadName = `${base} (${n})${suffix}`;
    }
    usedNames.add(downloadName.toLowerCase());
  }
  return downloadName;
}
